import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { login as apiLogin, loginWithTotp, sendLoginEmailCode, type TwoFactorMethod } from '../api';
import { useAuth } from '../context/AuthContext';
import TotpPrompt from '../components/TotpPrompt';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // 2FA-trin: hvis backend returnerer pre_auth_token, viser vi TOTP-prompt
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [methods, setMethods] = useState<TwoFactorMethod[]>([]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiLogin(email, password);
      if ('requires_2fa' in res && res.requires_2fa) {
        setPreAuthToken(res.pre_auth_token);
        setMethods(res.methods);
        // Hvis kun email er aktiv: udløs en kode med det samme så brugeren ikke
        // skal klikke en ekstra knap
        if (res.methods.length === 1 && res.methods[0] === 'email') {
          await sendLoginEmailCode(res.pre_auth_token);
        }
      } else {
        login(res.token, res.user);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login fejlede');
    } finally {
      setLoading(false);
    }
  }

  async function handleTotpSubmit(code: string) {
    if (!preAuthToken) return;
    const { token, user } = await loginWithTotp(preAuthToken, code);
    login(token, user);
  }

  async function handleSendEmailCode() {
    if (!preAuthToken) return;
    await sendLoginEmailCode(preAuthToken);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight" style={{ letterSpacing: '-0.025em' }}>
            <span style={{ color: '#131916' }}>RentalFleet</span>
            <span className="text-sage-700">OS</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Log ind for at fortsætte</p>
        </div>

        {preAuthToken ? (
          <TotpPrompt
            modal={false}
            title="To-faktor bekræftelse"
            methods={methods}
            onSubmit={handleTotpSubmit}
            onSendEmailCode={methods.includes('email') ? handleSendEmailCode : undefined}
            onCancel={() => { setPreAuthToken(null); setMethods([]); setPassword(''); }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
                placeholder="din@email.dk"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adgangskode</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-100)] border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 text-white font-medium rounded-lg px-4 py-2 text-sm transition-colors"
            >
              {loading ? 'Logger ind…' : 'Log ind'}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          Har du ikke en konto? <Link to="/signup" className="text-[var(--color-primary)] hover:underline">Opret konto</Link>
        </p>
      </div>
    </div>
  );
}
